import { useMemo, useState } from 'react';
import type { BrouillonItem, ChangementItem, Item, MembrePublic } from '@camellie/shared';
import { CATEGORIES_ENFANT, estEnfant, jourDeEcheance, pourEnfant } from '@camellie/shared';
import { FicheItem } from '../composants/FicheItem';
import { aujourdhui, FUSEAU_FAMILLE, majuscule } from '../lib/format';

interface Props {
  items: Item[];
  membres: MembrePublic[];
  moi: MembrePublic;
  onBasculer: (id: string) => void;
  onAjouter: (brouillon: BrouillonItem) => void;
  onModifier: (id: string, champs: ChangementItem) => void;
  onSupprimer: (id: string) => void;
}

const fJour = new Intl.DateTimeFormat('fr-CA', {
  timeZone: 'UTC',
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

function decaler(jour: string, n: number): string {
  const d = new Date(jour + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * L'ecran Enfants : Camille et Ellie cote a cote, sur une journee.
 *
 * Deux colonnes plutot qu'un enfant a la fois, parce que la question du matin
 * est « qu'est-ce qu'il reste a preparer », pas « que se passe-t-il pour
 * Camille ». La comparaison doit etre immediate.
 *
 * Aucun filtre Moi/Tout ici : preparer les enfants n'est assigne a personne
 * en particulier, c'est le travail du matin, fait par qui est debout.
 *
 * Les fleches permettent de preparer la veille au soir — c'est a ce
 * moment-la qu'on sort les vetements et qu'on fait les lunchs, pas a 6 h 30.
 */
export function EcranEnfants({
  items,
  membres,
  moi,
  onBasculer,
  onAjouter,
  onModifier,
  onSupprimer,
}: Props) {
  const aujourd = aujourdhui();
  const [jour, setJour] = useState(aujourd);
  const [enEdition, setEnEdition] = useState<string | null>(null);
  const [creation, setCreation] = useState<MembrePublic | null>(null);

  const enfants = useMemo(() => membres.filter(estEnfant), [membres]);

  /** Les elements du jour, ranges par enfant puis par categorie. */
  const parEnfant = useMemo(() => {
    const carte = new Map<string, Map<string, Item[]>>();
    for (const enfant of enfants) carte.set(enfant.id, new Map());

    for (const item of items) {
      if (item.statut === 'annule') continue;
      const enfantId = pourEnfant(item);
      if (!enfantId) continue;
      const cases = carte.get(enfantId);
      if (!cases) continue;
      if (!item.du_le || jourDeEcheance(item.du_le, FUSEAU_FAMILLE) !== jour) continue;

      const liste = cases.get(item.type);
      if (liste) liste.push(item);
      else cases.set(item.type, [item]);
    }
    return carte;
  }, [items, enfants, jour]);

  const item = enEdition ? (items.find((i) => i.id === enEdition) ?? null) : null;
  const enfantDeLItem = item ? enfants.find((e) => e.id === pourEnfant(item)) : undefined;

  if (enfants.length === 0) {
    return (
      <>
        <div className="entete">
          <h1>Enfants</h1>
        </div>
        <div className="contenu">
          <div className="vide">
            <strong>Aucun enfant enregistre.</strong>
            La migration 0003 ajoute Camille et Ellie.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="entete">
        <h1>Enfants</h1>
        <div className="entete-actions">
          <button type="button" className="fleche" onClick={() => setJour(decaler(jour, -1))} aria-label="Jour precedent">
            ‹
          </button>
          <button type="button" className="fleche" onClick={() => setJour(decaler(jour, 1))} aria-label="Jour suivant">
            ›
          </button>
        </div>
      </div>

      <div className="contenu">
        <p className="jour-choisi">
          {jour === aujourd
            ? 'Aujourd hui'
            : jour === decaler(aujourd, 1)
              ? 'Demain'
              : majuscule(fJour.format(new Date(jour + 'T12:00:00Z')))}
          {jour !== aujourd && (
            <button type="button" className="bouton discret" onClick={() => setJour(aujourd)}>
              Aujourd hui
            </button>
          )}
        </p>

        <div className="colonnes-enfants">
          {enfants.map((enfant) => {
            const cases = parEnfant.get(enfant.id) ?? new Map<string, Item[]>();
            const total = [...cases.values()].flat();
            const restant = total.filter((i) => i.statut === 'todo').length;

            return (
              <div key={enfant.id} className="colonne-enfant">
                <div className="tete-enfant">
                  <span className="point-membre" style={{ background: enfant.couleur }} />
                  <strong>{enfant.prenom}</strong>
                  <span className="reste">{restant === 0 ? 'prete' : restant + ' a faire'}</span>
                </div>

                {CATEGORIES_ENFANT.map(({ type, libelle }) => {
                  const liste = (cases.get(type) ?? []).sort((a, b) => a.cree_le.localeCompare(b.cree_le));
                  if (liste.length === 0) return null;
                  return (
                    <div key={type} className="bloc-enfant">
                      <h3>{libelle}</h3>
                      {liste.map((i) => (
                        <div key={i.id} className={'mini-ligne' + (i.statut === 'fait' ? ' faite' : '')}>
                          <button
                            type="button"
                            className={'mini-case' + (i.statut === 'fait' ? ' cochee' : '')}
                            onClick={() => onBasculer(i.id)}
                            aria-pressed={i.statut === 'fait'}
                            aria-label={(i.statut === 'fait' ? 'Decocher ' : 'Cocher ') + i.titre}
                          >
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                              <polyline points="4 12.5 9.5 18 20 6.5" />
                            </svg>
                          </button>
                          <button type="button" className="mini-titre" onClick={() => setEnEdition(i.id)}>
                            {i.titre}
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                })}

                {total.length === 0 && <p className="rien">Rien de prevu.</p>}

                <button type="button" className="ajouter-mini" onClick={() => setCreation(enfant)}>
                  + Ajouter
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {(item || creation) && (
        <FicheItem
          item={item}
          enfant={enfantDeLItem ?? creation ?? undefined}
          typeNouveau="vetement"
          dateParDefaut={jour}
          membres={membres}
          moi={moi.id}
          onEnregistrer={onModifier}
          onCreer={onAjouter}
          onSupprimer={onSupprimer}
          onFermer={() => {
            setEnEdition(null);
            setCreation(null);
          }}
        />
      )}
    </>
  );
}
